"use client";

import { getConsent } from "@/lib/consent";
import {
	createContext,
	useCallback,
	useContext,
	useEffect,
	useRef,
	useState,
} from "react";
import { type VidentBrowser, createVidentBrowser } from "vident-browser";

const VidentContext = createContext<VidentBrowser | null>(null);

export function useVident(): VidentBrowser | null {
	return useContext(VidentContext);
}

export function useRumEvent() {
	const vident = useContext(VidentContext);
	return useCallback(
		(name: string, properties?: Record<string, unknown>) => {
			vident?.trackEvent(name, properties);
		},
		[vident],
	);
}

export function useRumPageView(
	name: string,
	properties?: Record<string, unknown>,
) {
	const vident = useContext(VidentContext);
	const tracked = useRef(false);
	const propsRef = useRef(properties);
	propsRef.current = properties;
	useEffect(() => {
		if (!vident || tracked.current) {
			return;
		}
		tracked.current = true;
		vident.trackEvent(name, propsRef.current);
	}, [vident, name]);
}

const API_KEY = process.env.NEXT_PUBLIC_VIDENT_API_KEY;

const ANONYMOUS_CONFIG = {
	trackClicks: false,
	trackErrors: false,
	trackResources: false,
	replay: { enabled: false },
} as const;

const FULL_CONFIG = {
	replay: { enabled: true, sampleRate: 1.0, onErrorSampleRate: 1.0 },
} as const;

function createInstance(consented: boolean): VidentBrowser | null {
	if (!API_KEY) {
		return null;
	}
	return createVidentBrowser({
		apiKey: API_KEY,
		appName: "sendpigeon-web",
		storage: "sessionStorage",
		privacyMode: "strict",
		...(consented ? FULL_CONFIG : ANONYMOUS_CONFIG),
	});
}

export function RumProvider({ children }: { children: React.ReactNode }) {
	const [vident, setVident] = useState<VidentBrowser | null>(null);
	const initialized = useRef(false);

	useEffect(() => {
		if (initialized.current) {
			return;
		}
		initialized.current = true;

		const consented = getConsent() === "granted";
		setVident(createInstance(consented));

		function handleConsentChange(e: Event) {
			const detail = (e as CustomEvent).detail;
			if (detail === "granted") {
				setVident(createInstance(true));
			}
		}

		window.addEventListener("consent-changed", handleConsentChange);
		return () =>
			window.removeEventListener("consent-changed", handleConsentChange);
	}, []);

	return (
		<VidentContext.Provider value={vident}>{children}</VidentContext.Provider>
	);
}
